import React, { useState, useEffect, useContext, useMemo } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import { hasAdminRights } from '../utils/roleUtils';
import AddBookingModal from './AddBookingModal';
import EventModal from './EventModal';
import {
  CurrencyEuroIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  ClockIcon,
  UserIcon,
  CakeIcon,
  ShieldCheckIcon,
  BeakerIcon,
  TicketIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CheckIcon,
  ChevronRightIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import { FaDog } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const DogIcon: React.FC<{ className?: string }> = ({ className }) => {
  const Icon = FaDog as React.ComponentType<{ className?: string }>;
  return <Icon className={className} />;
};

interface DogIssue {
  dog_id: string;
  hundename: string;
  ownerName: string;
  ownerId: string;
  insuranceIssue: boolean;
  vaccinationIssue: boolean;
}

interface EventItem {
  id: string;
  title: string;
  start: string;
  start_time: string;
  participantCount: number;
  max_teilnehmer: number;
  creator: string | null;
  creator_name: string;
  cotrainer: string | null;
  cotrainer_name: string | null;
  eventType: 'booked' | 'created';
}

interface Birthday {
  mg_nr: string;
  vorname: string;
  name: string;
  geb_am: string;
}

interface Booking {
  id: number;
  buchungstag: string;
  buchungstext: string;
  betrag: number;
  konto_name: string;
}

interface BalanceData {
  totalBalance: number;
  totalIncome: number;
  totalExpense: number;
  saldo: number;
  recentBookings: Booking[];
}

interface GuestCardStats {
  totalSoldCards: number;
  activeGuestCards: number;
  totalRevenue: number;
  paidRevenue: number;
  totalBookedTickets: number;
  bookedThisMonth: number;
  soldThisMonth: number;
  unpaidBookingMembers?: any[];
}

const formatCurrency = (amount: number, decimals = 0) =>
  new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);

const formatDateShort = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
};

const formatTime = (time: string) => {
  if (!time) return '';
  const [h, m] = time.split(':');
  return `${h}:${m}`;
};

const Dashboard: React.FC = () => {
  const { user, BASE_URL } = useContext(AuthContext);
  const navigate = useNavigate();
  const hasAccess = hasAdminRights(user);

  const [loading, setLoading] = useState(true);
  const [membersCount, setMembersCount] = useState(0);
  const [guestsCount, setGuestsCount] = useState(0);
  const [openPayments, setOpenPayments] = useState(0);
  const [unpaidGuests, setUnpaidGuests] = useState(0);
  const [problemDogs, setProblemDogs] = useState<DogIssue[]>([]);
  const [totalDogs, setTotalDogs] = useState(0);
  const [events, setEvents] = useState<EventItem[]>([]);
  const [birthdays, setBirthdays] = useState<Birthday[]>([]);
  const [balance, setBalance] = useState<BalanceData | null>(null);
  const [guestCardStats, setGuestCardStats] = useState<GuestCardStats | null>(null);
  const [showAddBookingModal, setShowAddBookingModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const currentYear = new Date().getFullYear();

  const getMemberImageUrl = (memberNr: string | null, size = 'S') => {
    if (!memberNr) return null;
    const token = localStorage.getItem('jwt_token');
    return `${BASE_URL}/api/members/user-image-url/${memberNr}?size=${size}&token=${token}`;
  };

  const getDogImageUrl = (dogId: string) => {
    const token = localStorage.getItem('jwt_token');
    if (!token || !BASE_URL) return null;
    return `${BASE_URL}/api/dogs/image-url/${dogId}?size=S&token=${token}`;
  };

  useEffect(() => {
    if (!hasAccess || !user) {
      setLoading(false);
      return;
    }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess, user]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const month = new Date().getMonth() + 1;
      const nextMonth = month === 12 ? 1 : month + 1;

      const [membersRes, dogsRes, drinksRes, guestStatsRes, eventsRes, bdRes1, bdRes2, balanceRes] =
        await Promise.all([
          apiClient.get('/api/members').catch(() => null),
          apiClient.get('/api/members/all_dogs_status').catch(() => null),
          apiClient.get('/api/drinks').catch(() => null),
          apiClient.get(`/api/guest-cards/stats?year=${currentYear}`).catch(() => null),
          apiClient.get('/api/calendar/user-events-calendar/unknown').catch(() => null),
          apiClient.get(`/api/members/birthdays?month=${month}`).catch(() => null),
          apiClient.get(`/api/members/birthdays?month=${nextMonth}`).catch(() => null),
          apiClient.get('/api/finance/dashboard-balance').catch(() => null),
        ]);

      // Members & guests
      if (membersRes?.data) {
        const all = Array.isArray(membersRes.data) ? membersRes.data : [];
        setMembersCount(
          all.filter((m: any) => m.roles?.some((r: any) => r.name !== 'guest')).length
        );
        setGuestsCount(
          all.filter((m: any) => m.roles?.length === 1 && m.roles[0].name === 'guest').length
        );
      }

      // Dogs status
      if (dogsRes?.data?.success && dogsRes.data?.data?.members) {
        const md = dogsRes.data.data.members;
        const list: DogIssue[] = [];
        let total = 0;
        md.forEach((m: any) => {
          m.dogs?.forEach((d: any) => {
            total++;
            if (!d.valid_insurance || !d.valid_tollwut) {
              list.push({
                dog_id: d.dog_id,
                hundename: d.hundename || 'Unbenannt',
                ownerName: m.member_name,
                ownerId: m.member_nr,
                insuranceIssue: !d.valid_insurance,
                vaccinationIssue: !d.valid_tollwut,
              });
            }
          });
        });
        setTotalDogs(total);
        setProblemDogs(list);
      }

      // Drinks (offene Verzehrkosten)
      if (drinksRes?.data) {
        const unpaid = drinksRes.data.filter((d: any) => !d.paid);
        const total = unpaid.reduce((s: number, d: any) => s + (parseFloat(d.amount) || 0), 0);
        setOpenPayments(total);
      }

      // Guest cards
      if (guestStatsRes?.data?.success) {
        setGuestCardStats(guestStatsRes.data.stats);
        setUnpaidGuests(guestStatsRes.data.stats?.unpaidBookingMembers?.length || 0);
      }

      // Events: today + upcoming, max 5
      if (eventsRes?.data) {
        const all: any[] = [
          ...(Array.isArray(eventsRes.data.eventsBooked)
            ? eventsRes.data.eventsBooked.map((e: any) => ({ ...e, eventType: 'booked' }))
            : []),
          ...(Array.isArray(eventsRes.data.eventsCreated)
            ? eventsRes.data.eventsCreated.map((e: any) => ({ ...e, eventType: 'created' }))
            : []),
        ];
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const todayE = all.filter((e: any) => {
          if (!e?.start) return false;
          const d = new Date(e.start);
          return d.toDateString() === today.toDateString();
        });
        const upcoming = all
          .filter((e: any) => e?.start && e?.start_time && new Date(`${e.start}T${e.start_time}`) > now)
          .sort(
            (a: any, b: any) =>
              new Date(`${a.start}T${a.start_time}`).getTime() -
              new Date(`${b.start}T${b.start_time}`).getTime()
          );
        const combined = [...todayE.slice(0, 2), ...upcoming.slice(0, 5 - Math.min(todayE.length, 2))]
          .slice(0, 5)
          .map((e: any) => ({
            id: e.id || `tmp-${Math.random()}`,
            title: e.title || 'Unbenannter Termin',
            start: e.start,
            start_time: e.start_time,
            participantCount: e.participantCount || 0,
            max_teilnehmer: e.max_teilnehmer || 0,
            creator: e.creator || null,
            creator_name: e.creator_name || 'Unbekannt',
            cotrainer: e.cotrainer || null,
            cotrainer_name: e.cotrainer_name || null,
            eventType: e.eventType || 'booked',
          }));
        setEvents(combined);
      }

      // Birthdays
      const bds = [...(bdRes1?.data || []), ...(bdRes2?.data || [])];
      setBirthdays(bds);

      // Balance
      if (balanceRes?.data) setBalance(balanceRes.data);
    } catch (e) {
      console.error('Dashboard fetch error', e);
    } finally {
      setLoading(false);
      setLastUpdated(new Date());
    }
  };

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return 'Guten Morgen';
    if (h < 18) return 'Guten Tag';
    return 'Guten Abend';
  }, []);

  const formatLastUpdated = () => {
    const diff = Math.floor((Date.now() - lastUpdated.getTime()) / 60000);
    if (diff < 1) return 'Gerade aktualisiert';
    if (diff < 60) return `Vor ${diff} Min.`;
    return lastUpdated.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) + ' Uhr';
  };

  // === Compliance ring ===
  const validDogs = totalDogs - problemDogs.length;
  const validPercent = totalDogs > 0 ? Math.round((validDogs / totalDogs) * 100) : 100;
  const ringCircumference = 276.46; // r=44
  const ringOffset = ringCircumference * (1 - validPercent / 100);
  const ringColor =
    validPercent === 100 ? '#16a34a' : validPercent >= 75 ? '#f59e0b' : '#ef4444';
  const complianceTone =
    validPercent === 100 ? 'primary' : validPercent >= 75 ? 'warning' : 'error';

  // === Birthdays computed ===
  const upcomingBirthdays = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now);
    todayStart.setHours(0, 0, 0, 0);
    return birthdays
      .map((m) => {
        const d = new Date(m.geb_am);
        d.setFullYear(now.getFullYear());
        if (d < todayStart) d.setFullYear(now.getFullYear() + 1);
        return { ...m, adjustedDate: d };
      })
      .filter((m: any) => m.adjustedDate >= todayStart)
      .sort((a: any, b: any) => a.adjustedDate.getTime() - b.adjustedDate.getTime())
      .slice(0, 4);
  }, [birthdays]);

  // === Tasks queue ===
  const tasks = useMemo(() => {
    const t: { id: string; title: string; sub: string; severity: 'critical' | 'warning'; href: string; meta?: string }[] = [];
    const critical = problemDogs.filter((d) => d.insuranceIssue && d.vaccinationIssue);
    if (critical.length > 0) {
      t.push({
        id: 'dogs-critical',
        title: `${critical.length} ${critical.length === 1 ? 'Hund' : 'Hunde'} ohne Versicherung & Tollwut`,
        sub: 'Trainingssperre · sofort handeln',
        severity: 'critical',
        href: '/dog-management-beta',
        meta: 'SOFORT',
      });
    }
    const partial = problemDogs.length - critical.length;
    if (partial > 0) {
      t.push({
        id: 'dogs-partial',
        title: `${partial} ${partial === 1 ? 'Hund' : 'Hunde'} mit unvollständigen Dokumenten`,
        sub: 'Versicherung oder Tollwut fehlt',
        severity: 'warning',
        href: '/dog-management-beta',
        meta: `${partial} OFFEN`,
      });
    }
    if (unpaidGuests > 0) {
      t.push({
        id: 'guest-unpaid',
        title: `${unpaidGuests} ${unpaidGuests === 1 ? 'Gast' : 'Gäste'} mit unbezahlter Karte`,
        sub: 'Buchungen ohne Bezahlung',
        severity: 'warning',
        href: '/guest-cards',
        meta: `${unpaidGuests} OFFEN`,
      });
    }
    if (openPayments > 50) {
      t.push({
        id: 'drinks',
        title: `${formatCurrency(openPayments)} Verzehr offen`,
        sub: 'Offene Bewirtungs-Beträge',
        severity: 'warning',
        href: '/drinks-management',
        meta: 'CHECK',
      });
    }
    return t;
  }, [problemDogs, unpaidGuests, openPayments]);

  const criticalTasks = tasks.filter((t) => t.severity === 'critical').length;
  const warningTasks = tasks.filter((t) => t.severity === 'warning').length;

  // === Top-level alert tone ===
  const alertTone: 'critical' | 'warning' | 'ok' =
    criticalTasks > 0 ? 'critical' : warningTasks > 0 ? 'warning' : 'ok';

  const handleEventClick = (event: EventItem, e: React.MouseEvent) => {
    e.stopPropagation();
    const [sh, sm] = (event.start_time || '00:00').split(':');
    const start = new Date(event.start);
    start.setHours(parseInt(sh), parseInt(sm), 0, 0);
    const end = new Date(start);
    end.setHours(start.getHours() + 1);
    setSelectedEvent({
      id: event.id,
      title: event.title,
      start,
      end,
      extendedProps: {
        color: '#9333ea',
        bookable: event.max_teilnehmer || 0,
        bookingCount: event.participantCount || 0,
        creator: event.creator ? { name: event.creator_name, id: event.creator } : null,
        cotrainer: event.cotrainer ? { name: event.cotrainer_name, id: event.cotrainer } : null,
      },
    });
  };

  const totalBalance = balance?.totalBalance || 0;
  const yearBalance = balance?.saldo || 0;
  const recentBookings = balance?.recentBookings?.slice(0, 4) || [];

  // ============== NON-ACCESS VIEW ==============
  if (!hasAccess) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="rounded-2xl border border-info-200 dark:border-info-800/60 bg-info-50 dark:bg-info-900/20 p-6">
          <p className="eyebrow text-info-600 dark:text-info-400 mb-1 text-[10px] font-bold uppercase tracking-[0.18em]">
            Zugriff begrenzt
          </p>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
            Dashboard nur für Vorstand & Trainer
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Vollständige Statistiken sind nur für Vorstandsmitglieder und Trainer sichtbar.
          </p>
        </div>
      </div>
    );
  }

  // ============== COCKPIT VIEW ==============
  return (
    <div className="max-w-7xl mx-auto pb-12">
      {/* Story-Hero — Vorstands-Cockpit (kein Foto, nur Text + Action rechts) */}
      <section className="pt-2 pb-4">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.22em] text-amber-600/80 dark:text-amber-400/80 font-semibold mb-3 flex items-center gap-2">
              <span className={`w-1.5 h-1.5 rounded-full ${alertTone === 'critical' ? 'bg-error-500' : alertTone === 'warning' ? 'bg-warning-500' : 'bg-primary-500'} animate-pulse`} />
              Live · {greeting} · {formatLastUpdated()}
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white leading-[1.05] tracking-tight">
              Vorstands-<span className="text-amber-600 dark:text-amber-400">Cockpit.</span>
            </h1>
            <p className="text-gray-700 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl mt-4">
              {loading ? (
                <>Daten werden geladen…</>
              ) : (
                <>
                  <span className="font-bold text-gray-900 dark:text-white">{membersCount} Mitglieder</span> ·{' '}
                  <span className="font-bold text-gray-900 dark:text-white">{guestsCount} Gäste</span> ·{' '}
                  Compliance bei{' '}
                  <span className={`font-bold ${complianceTone === 'primary' ? 'text-primary-700 dark:text-primary-300' : complianceTone === 'warning' ? 'text-amber-700 dark:text-amber-300' : 'text-rose-700 dark:text-rose-300'}`}>
                    {validPercent}%
                  </span>
                  {openPayments > 0 ? (
                    <>, noch <span className="font-bold text-amber-700 dark:text-amber-300">{formatCurrency(openPayments)}</span> Verzehr offen.</>
                  ) : (
                    <>, alle Verzehrkosten beglichen.</>
                  )}
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={fetchAll}
              className="px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-[0.18em] text-gray-700 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
            >
              Refresh
            </button>
            <button
              onClick={() => setShowAddBookingModal(true)}
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 bg-primary-500 hover:bg-primary-400 text-slate-950 rounded-xl font-bold text-sm transition"
            >
              <CurrencyEuroIcon className="h-5 w-5" />
              <span>Buchung</span>
            </button>
          </div>
        </div>
      </section>

      {/* === KPI STRIP === */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KpiCard label="Mitglieder" value={loading ? '…' : membersCount} accent="primary" />
        <KpiCard label="Gäste" value={loading ? '…' : guestsCount} accent="info" />
        <KpiCard
          label="Verzehr offen"
          value={loading ? '…' : formatCurrency(openPayments)}
          accent={openPayments > 0 ? 'warning' : 'primary'}
        />
        <KpiCard
          label="Hunde-Compliance"
          value={loading ? '…' : `${validPercent}%`}
          sub={loading ? '' : `${validDogs}/${totalDogs} OK`}
          accent={complianceTone as any}
        />
      </div>

      {/* === FULL-WIDTH AUFGABEN QUEUE === */}
      {tasks.length > 0 && !loading && (
        <div
          className={`relative rounded-2xl border mb-4 overflow-hidden bg-white dark:bg-[#1c2432] ${
            alertTone === 'critical'
              ? 'border-error-500 dark:border-error-500'
              : 'border-warning-400 dark:border-warning-500/60'
          }`}
        >
          <div
            className={`absolute inset-y-0 left-0 w-1 ${
              alertTone === 'critical' ? 'bg-error-500' : 'bg-warning-500'
            }`}
          />
          <div className="p-4 sm:p-5 pl-5 sm:pl-6">
            {/* Header row */}
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <div
                  className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 border ${
                    alertTone === 'critical'
                      ? 'bg-error-500/15 border-error-500/50'
                      : 'bg-warning-500/15 border-warning-500/40'
                  }`}
                >
                  <ExclamationTriangleIcon
                    className={`w-5 h-5 ${
                      alertTone === 'critical' ? 'text-error-500' : 'text-warning-500'
                    }`}
                  />
                </div>
                <div>
                  <Eyebrow tone={alertTone === 'critical' ? 'error' : 'warning'}>
                    Aufgaben · {tasks.length} offen
                  </Eyebrow>
                  <h3 className="text-sm sm:text-base font-extrabold text-gray-900 dark:text-white mt-0.5">
                    {alertTone === 'critical'
                      ? 'Vereinsbetrieb beeinträchtigt — sofort handeln'
                      : 'Es gibt offene Vorgänge'}
                  </h3>
                </div>
              </div>
              <div className="flex items-center gap-1.5 text-[9px]">
                {criticalTasks > 0 && (
                  <span className="px-2 py-1 rounded bg-error-500/20 text-error-600 dark:text-error-400 font-bold border border-error-500/40 tracking-wider uppercase">
                    {criticalTasks} critical
                  </span>
                )}
                {warningTasks > 0 && (
                  <span className="px-2 py-1 rounded bg-warning-500/20 text-warning-700 dark:text-warning-400 font-bold border border-warning-500/40 tracking-wider uppercase">
                    {warningTasks} normal
                  </span>
                )}
              </div>
            </div>

            {/* Horizontal task grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
              {tasks.map((task) => (
                <button
                  key={task.id}
                  onClick={() => navigate(task.href)}
                  className={`text-left rounded-xl p-3 flex items-start gap-2.5 border transition-colors ${
                    task.severity === 'critical'
                      ? 'bg-error-50 dark:bg-error-500/5 border-error-300 dark:border-error-500/50 hover:border-error-500'
                      : 'bg-warning-50 dark:bg-warning-500/5 border-warning-300 dark:border-warning-500/40 hover:border-warning-500'
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 border ${
                      task.severity === 'critical'
                        ? 'bg-error-500/20 border-error-500/50'
                        : 'bg-warning-500/15 border-warning-500/40'
                    }`}
                  >
                    <ExclamationTriangleIcon
                      className={`w-4 h-4 ${
                        task.severity === 'critical' ? 'text-error-500' : 'text-warning-500'
                      }`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <span className="text-xs font-extrabold text-gray-900 dark:text-white">
                        {task.title}
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-600 dark:text-gray-400 mb-1.5 leading-snug">
                      {task.sub}
                    </p>
                    {task.meta && (
                      <span
                        className={`inline-block text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded ${
                          task.severity === 'critical'
                            ? 'bg-error-500 text-white'
                            : 'bg-warning-500/30 text-warning-700 dark:text-warning-400'
                        }`}
                      >
                        {task.meta}
                      </span>
                    )}
                  </div>
                  <ChevronRightIcon className="w-3.5 h-3.5 text-gray-400 dark:text-gray-600 flex-shrink-0 mt-1" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* === COCKPIT 12-COL GRID === */}
      <div className="grid grid-cols-12 gap-3 sm:gap-4">
        {/* ZONE 1 — HUNDE-COMPLIANCE STATUS (col-4) */}
        <CockpitCard className="col-span-12 md:col-span-4" onClick={() => navigate('/dog-management-beta')}>
          <CardHeader
            eyebrow="Hunde · Compliance"
            eyebrowTone={complianceTone as any}
            right={<span className="text-[10px] text-gray-400 dark:text-gray-500">{totalDogs} ges.</span>}
          />
          <div className="flex items-start gap-4">
            <div className="relative flex-shrink-0">
              <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="44"
                  className="stroke-gray-100 dark:stroke-slate-800"
                  strokeWidth="6"
                  fill="none"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="44"
                  stroke={ringColor}
                  strokeWidth="6"
                  fill="none"
                  strokeDasharray={ringCircumference}
                  strokeDashoffset={ringOffset}
                  strokeLinecap="round"
                  style={{ transition: 'stroke-dashoffset 600ms ease' }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-extrabold text-gray-900 dark:text-white tabular-nums">
                  {validPercent}%
                </span>
                <span className="text-[9px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  vollst.
                </span>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="text-sm font-extrabold text-gray-900 dark:text-white">
                {validDogs} von {totalDogs} OK
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                {problemDogs.length === 0
                  ? 'Alle Hunde im grünen Bereich'
                  : `${problemDogs.length} unvollständig`}
              </div>
              <div className="space-y-1.5 text-[11px]">
                <ComplianceRow
                  label="Versicherung"
                  value={`${totalDogs - problemDogs.filter((d) => d.insuranceIssue).length}/${totalDogs}`}
                  ok={problemDogs.filter((d) => d.insuranceIssue).length === 0}
                />
                <ComplianceRow
                  label="Tollwut"
                  value={`${totalDogs - problemDogs.filter((d) => d.vaccinationIssue).length}/${totalDogs}`}
                  ok={problemDogs.filter((d) => d.vaccinationIssue).length === 0}
                />
              </div>
            </div>
          </div>

          <div className="my-4 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-slate-700 to-transparent" />

          {problemDogs.length > 0 ? (
            <>
              <Eyebrow>Unvollständig</Eyebrow>
              <div className="space-y-1 mt-2">
                {problemDogs.slice(0, 3).map((dog) => (
                  <div
                    key={dog.dog_id}
                    className="flex items-center gap-2.5 p-1.5 -mx-1 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-lg overflow-hidden bg-gray-100 dark:bg-slate-800 flex-shrink-0 border border-gray-200 dark:border-slate-700">
                      {dog.dog_id ? (
                        <img
                          src={getDogImageUrl(dog.dog_id) || ''}
                          alt={dog.hundename}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const t = e.target as HTMLImageElement;
                            t.style.display = 'none';
                            const next = t.nextSibling as HTMLElement;
                            if (next) next.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className="w-full h-full bg-warning-50 dark:bg-warning-900/30 items-center justify-center"
                        style={{ display: dog.dog_id ? 'none' : 'flex' }}
                      >
                        <DogIcon className="h-4 w-4 text-warning-500" />
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">
                        {dog.hundename}
                      </p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-500 truncate">
                        {dog.ownerName}
                      </p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {dog.insuranceIssue && (
                        <span
                          className="p-1 rounded bg-warning-100 dark:bg-warning-900/30"
                          title="Versicherung fehlt"
                        >
                          <ShieldCheckIcon className="h-3 w-3 text-warning-600 dark:text-warning-400" />
                        </span>
                      )}
                      {dog.vaccinationIssue && (
                        <span
                          className="p-1 rounded bg-warning-100 dark:bg-warning-900/30"
                          title="Tollwut fehlt"
                        >
                          <BeakerIcon className="h-3 w-3 text-warning-600 dark:text-warning-400" />
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {problemDogs.length > 3 && (
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 text-center pt-1">
                    +{problemDogs.length - 3} weitere
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-3 text-center">
              <div className="w-9 h-9 rounded-xl bg-primary-500/15 border border-primary-500/30 flex items-center justify-center mb-1.5">
                <CheckIcon className="h-5 w-5 text-primary-500 dark:text-primary-400" strokeWidth={3} />
              </div>
              <p className="text-xs font-bold text-gray-900 dark:text-white">Alles vollständig</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-500">Keine offenen Dokumente</p>
            </div>
          )}
        </CockpitCard>

        {/* ZONE 2 — NÄCHSTE TERMINE (col-5) */}
        <CockpitCard className="col-span-12 md:col-span-5 grid-bg">
          <CardHeader
            eyebrow="Nächste Termine"
            eyebrowTone="primary"
            right={
              <button
                onClick={() => navigate('/my-bookings')}
                className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
              >
                Alle →
              </button>
            }
          />

          {events.length > 0 ? (
            <>
              {/* Hero: nächster Termin */}
              <div className="flex items-start gap-4 mb-4">
                <DateTile date={events[0].start} />
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-extrabold text-gray-900 dark:text-white mb-1 truncate">
                    {events[0].title}
                  </h3>
                  <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mb-2">
                    <span className="flex items-center gap-1">
                      <ClockIcon className="w-3.5 h-3.5" />
                      {formatTime(events[0].start_time)}
                    </span>
                    <span className="flex items-center gap-1">
                      <UserIcon className="w-3.5 h-3.5" />
                      {events[0].creator_name}
                    </span>
                  </div>
                  {events[0].max_teilnehmer > 0 && (
                    <>
                      <div className="flex justify-between text-[11px] mb-1">
                        <span className="text-gray-500 dark:text-gray-400">Plätze belegt</span>
                        <span className="font-bold text-gray-900 dark:text-white">
                          {events[0].participantCount} / {events[0].max_teilnehmer}
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-gray-100 dark:bg-slate-800 overflow-hidden">
                        <div
                          className={`h-full ${
                            events[0].participantCount >= events[0].max_teilnehmer
                              ? 'bg-error-500'
                              : 'bg-primary-500'
                          }`}
                          style={{
                            width: `${Math.min(
                              100,
                              (events[0].participantCount / events[0].max_teilnehmer) * 100
                            )}%`,
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              {events.length > 1 && (
                <>
                  <div className="my-3 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-slate-700 to-transparent" />
                  <Eyebrow>Weitere Termine</Eyebrow>
                  <div className="mt-2 space-y-1">
                    {events.slice(1, 4).map((event) => {
                      const isFull = event.participantCount >= event.max_teilnehmer;
                      const isCreated = event.eventType === 'created';
                      return (
                        <div
                          key={event.id}
                          onClick={(e) => handleEventClick(event, e)}
                          className="flex items-center gap-2.5 p-1.5 -mx-1 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
                        >
                          <div className="w-8 h-8 rounded-lg overflow-hidden bg-gray-100 dark:bg-slate-800 flex-shrink-0 border border-gray-200 dark:border-slate-700">
                            {event.creator && getMemberImageUrl(event.creator) ? (
                              <img
                                src={getMemberImageUrl(event.creator) || ''}
                                alt={event.creator_name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  const t = e.target as HTMLImageElement;
                                  t.style.display = 'none';
                                  const next = t.nextSibling as HTMLElement;
                                  if (next) next.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div
                              className="w-full h-full bg-primary-50 dark:bg-primary-900/30 items-center justify-center"
                              style={{
                                display: event.creator && getMemberImageUrl(event.creator) ? 'none' : 'flex',
                              }}
                            >
                              <UserIcon className="h-4 w-4 text-primary-500" />
                            </div>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">
                              {event.title}
                            </p>
                            <p className="text-[10px] text-gray-500 dark:text-gray-500">
                              {formatDateShort(event.start)} · {formatTime(event.start_time)} ·{' '}
                              {event.creator_name}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-0.5">
                            <span
                              className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                                isCreated
                                  ? 'bg-info-500/20 text-info-600 dark:text-info-400'
                                  : 'bg-primary-500/20 text-primary-600 dark:text-primary-400'
                              }`}
                            >
                              {isCreated ? 'Trainer' : 'Gebucht'}
                            </span>
                            <span
                              className={`text-[10px] tabular-nums ${
                                isFull
                                  ? 'text-error-500'
                                  : 'text-gray-400 dark:text-gray-500'
                              }`}
                            >
                              {event.participantCount}/{event.max_teilnehmer}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <CalendarIcon className="h-10 w-10 text-gray-200 dark:text-slate-700 mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-500">Keine Termine</p>
            </div>
          )}
        </CockpitCard>

        {/* ZONE 3 — GÄSTEKARTEN (col-3) */}
        <CockpitCard className="col-span-12 md:col-span-3" onClick={() => navigate('/guest-cards')}>
          <CardHeader
            eyebrow={`Gästekarten ${currentYear}`}
            eyebrowTone="info"
            icon={TicketIcon}
          />

          {/* Hero: Verkaufte Karten + Umsatz */}
          <div className="mb-4">
            <div className="flex items-baseline gap-2">
              <p className="text-3xl font-extrabold text-gray-900 dark:text-white tabular-nums leading-none">
                {guestCardStats?.totalSoldCards || 0}
              </p>
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-500">
                verkauft
              </span>
            </div>
            <p className="text-sm font-bold text-info-600 dark:text-info-400 tabular-nums mt-1">
              {formatCurrency(guestCardStats?.totalRevenue || 0)}
            </p>
            <p className="text-[10px] text-gray-500 dark:text-gray-500">Umsatz dieses Jahr</p>
          </div>

          {/* Diesen Monat */}
          <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/50 p-3 mb-3">
            <Eyebrow>Diesen Monat</Eyebrow>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <p className="text-lg font-extrabold text-gray-900 dark:text-white tabular-nums leading-none">
                  {guestCardStats?.soldThisMonth || 0}
                </p>
                <p className="text-[10px] text-gray-500 dark:text-gray-500 mt-1">neu verkauft</p>
              </div>
              <div>
                <p className="text-lg font-extrabold text-gray-900 dark:text-white tabular-nums leading-none">
                  {guestCardStats?.bookedThisMonth || 0}
                </p>
                <p className="text-[10px] text-gray-500 dark:text-gray-500 mt-1">Buchungen</p>
              </div>
            </div>
          </div>

          {/* Status Footer */}
          {unpaidGuests > 0 ? (
            <div className="flex items-center gap-2 rounded-lg border border-warning-300 dark:border-warning-500/40 bg-warning-50 dark:bg-warning-500/5 px-2.5 py-2">
              <ExclamationTriangleIcon className="w-3.5 h-3.5 text-warning-500 flex-shrink-0" />
              <span className="text-[11px] font-semibold text-warning-700 dark:text-warning-400">
                {unpaidGuests} unbezahlt
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-lg border border-primary-200 dark:border-primary-500/30 bg-primary-50 dark:bg-primary-500/5 px-2.5 py-2">
              <CheckIcon
                className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400 flex-shrink-0"
                strokeWidth={3}
              />
              <span className="text-[11px] font-semibold text-primary-700 dark:text-primary-400">
                Alle bezahlt
              </span>
            </div>
          )}
        </CockpitCard>

        {/* ZONE 4 — FINANZEN (kompakt, info-only) */}
        <CockpitCard className="col-span-12 lg:col-span-7" onClick={() => navigate('/account-overview')}>
          <CardHeader
            eyebrow="Finanzen · Vereinskasse"
            eyebrowTone="info"
            icon={BanknotesIcon}
            right={
              <div className="flex items-center gap-2">
                <span className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white tabular-nums leading-none">
                  {formatCurrency(totalBalance)}
                </span>
                <span
                  className={`flex items-center gap-0.5 text-[10px] font-bold tabular-nums ${
                    yearBalance >= 0
                      ? 'text-success-600 dark:text-success-400'
                      : 'text-error-600 dark:text-error-400'
                  }`}
                >
                  {yearBalance >= 0 ? (
                    <ArrowTrendingUpIcon className="h-3 w-3" />
                  ) : (
                    <ArrowTrendingDownIcon className="h-3 w-3" />
                  )}
                  {yearBalance >= 0 ? '+' : ''}
                  {formatCurrency(yearBalance)}
                </span>
              </div>
            }
          />

          {recentBookings.length > 0 ? (
            <div className="space-y-1">
              {recentBookings.map((b) => {
                const d = new Date(b.buchungstag);
                const day = d.getDate().toString().padStart(2, '0');
                const month = d
                  .toLocaleDateString('de-DE', { month: 'short' })
                  .toUpperCase()
                  .replace('.', '');
                return (
                  <div
                    key={b.id}
                    className="flex items-center gap-2.5 p-1.5 -mx-1 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gray-50 dark:bg-slate-950/60 border border-gray-200 dark:border-slate-700 flex flex-col items-center justify-center">
                      <span className="text-[12px] font-extrabold text-gray-900 dark:text-white leading-none tabular-nums">
                        {day}
                      </span>
                      <span className="text-[7px] uppercase tracking-wider text-gray-500 dark:text-gray-500 leading-tight mt-0.5 font-bold">
                        {month}
                      </span>
                    </div>
                    <p className="text-xs text-gray-900 dark:text-white truncate flex-1">
                      {b.buchungstext}
                    </p>
                    <span
                      className={`text-xs font-bold tabular-nums flex-shrink-0 ${
                        b.betrag >= 0
                          ? 'text-success-600 dark:text-success-400'
                          : 'text-error-600 dark:text-error-400'
                      }`}
                    >
                      {b.betrag >= 0 ? '+' : ''}
                      {formatCurrency(b.betrag, 2)}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-gray-400 dark:text-gray-600 py-2 text-center">
              Keine Buchungen
            </p>
          )}
        </CockpitCard>

        {/* ZONE 5 — GEBURTSTAGE (col-5) */}
        <CockpitCard className="col-span-12 lg:col-span-5" onClick={() => navigate('/members')}>
          <CardHeader
            eyebrow="Geburtstage · demnächst"
            eyebrowTone="primary"
            icon={CakeIcon}
            right={
              <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 tabular-nums">
                {upcomingBirthdays.length}
              </span>
            }
          />

          {upcomingBirthdays.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {upcomingBirthdays.map((m: any) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const diffDays = Math.round(
                  (m.adjustedDate.getTime() - today.getTime()) / 86400000
                );
                const isToday = diffDays === 0;
                const upcomingAge =
                  m.adjustedDate.getFullYear() - new Date(m.geb_am).getFullYear();
                const isJubilee = upcomingAge % 10 === 0 && upcomingAge > 0;
                const isSoon = diffDays <= 7 && !isToday;

                const weekday = m.adjustedDate
                  .toLocaleDateString('de-DE', { weekday: 'short' })
                  .toUpperCase();
                const day = m.adjustedDate.getDate().toString().padStart(2, '0');
                const month = m.adjustedDate
                  .toLocaleDateString('de-DE', { month: 'short' })
                  .toUpperCase()
                  .replace('.', '');

                const cardCls = isToday
                  ? '!border-primary-500 dark:!border-primary-500/60 bg-primary-50 dark:bg-primary-500/5'
                  : isJubilee
                  ? '!border-warning-400 dark:!border-warning-500/50 bg-warning-50/50 dark:bg-warning-500/5'
                  : 'border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/50';

                return (
                  <div
                    key={m.mg_nr}
                    className={`relative rounded-xl border p-3 transition-colors ${cardCls}`}
                  >
                    {/* Header row: weekday + day-month + status pill */}
                    <div className="flex items-start justify-between mb-2.5">
                      <div className="flex items-baseline gap-1.5">
                        <span
                          className={`text-[9px] font-bold uppercase tracking-wider ${
                            isToday
                              ? 'text-primary-600 dark:text-primary-400'
                              : isJubilee
                              ? 'text-warning-600 dark:text-warning-500'
                              : 'text-gray-400 dark:text-gray-500'
                          }`}
                        >
                          {weekday}
                        </span>
                        <span className="text-base font-extrabold text-gray-900 dark:text-white leading-none tabular-nums">
                          {day}
                        </span>
                        <span
                          className={`text-[9px] font-bold uppercase tracking-wider ${
                            isToday
                              ? 'text-primary-600 dark:text-primary-400'
                              : 'text-gray-400 dark:text-gray-500'
                          }`}
                        >
                          {month}
                        </span>
                      </div>
                      <span
                        className={`px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider tabular-nums ${
                          isToday
                            ? 'bg-primary-600 text-white'
                            : isSoon
                            ? 'bg-primary-500/15 text-primary-600 dark:text-primary-400 border border-primary-500/30'
                            : 'bg-white/60 dark:bg-slate-900 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-slate-700'
                        }`}
                      >
                        {isToday ? 'HEUTE' : `IN ${diffDays}T`}
                      </span>
                    </div>

                    {/* Avatar + Name + Age */}
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 border-2 ${
                          isToday
                            ? 'border-primary-500 dark:border-primary-500/60'
                            : isJubilee
                            ? 'border-warning-400 dark:border-warning-500/50'
                            : 'border-gray-200 dark:border-slate-700'
                        }`}
                      >
                        {getMemberImageUrl(m.mg_nr) ? (
                          <img
                            src={getMemberImageUrl(m.mg_nr) || ''}
                            alt={`${m.vorname} ${m.name}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const t = e.target as HTMLImageElement;
                              t.style.display = 'none';
                              const next = t.nextSibling as HTMLElement;
                              if (next) next.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className="w-full h-full bg-primary-50 dark:bg-primary-900/30 items-center justify-center"
                          style={{ display: getMemberImageUrl(m.mg_nr) ? 'none' : 'flex' }}
                        >
                          <UserIcon className="h-4 w-4 text-primary-500 dark:text-primary-400" />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-extrabold text-gray-900 dark:text-white truncate leading-tight">
                          {m.vorname} {m.name}
                        </p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className="text-[10px] text-gray-500 dark:text-gray-400 tabular-nums">
                            wird <span className="font-bold text-gray-900 dark:text-white">{upcomingAge}</span>
                          </span>
                          {isJubilee && (
                            <span className="px-1 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider bg-warning-500/20 text-warning-700 dark:text-warning-400">
                              Jubiläum
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <div className="w-10 h-10 rounded-xl bg-primary-500/15 border border-primary-500/30 flex items-center justify-center mb-2">
                <CakeIcon className="h-5 w-5 text-primary-500 dark:text-primary-400" />
              </div>
              <p className="text-xs font-bold text-gray-900 dark:text-white">Keine Geburtstage</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-500">in den nächsten 60 Tagen</p>
            </div>
          )}
        </CockpitCard>
      </div>

      {/* ===== AddBookingModal ===== */}
      <AddBookingModal
        isOpen={showAddBookingModal}
        onClose={() => setShowAddBookingModal(false)}
        onSuccess={() => {
          setShowAddBookingModal(false);
          fetchAll();
        }}
      />

      {/* ===== EventModal ===== */}
      {selectedEvent && (
        <EventModal
          event={selectedEvent}
          events={[]}
          onClose={() => setSelectedEvent(null)}
          onDelete={() => {
            setSelectedEvent(null);
            fetchAll();
          }}
          onBook={fetchAll}
          refreshCalendar={fetchAll}
          onEventSelect={setSelectedEvent}
          onEdit={() => {}}
        />
      )}

      {/* Local utility CSS for grid-bg */}
      <style>{`
        .grid-bg {
          background-image:
            linear-gradient(rgba(148,163,184,0.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(148,163,184,0.08) 1px, transparent 1px);
          background-size: 24px 24px;
        }
        .dark .grid-bg {
          background-image:
            linear-gradient(rgba(51,65,85,0.25) 1px, transparent 1px),
            linear-gradient(90deg, rgba(51,65,85,0.25) 1px, transparent 1px);
          background-size: 24px 24px;
        }
      `}</style>
    </div>
  );
};

// ====== SUB-COMPONENTS ======

const CockpitCard: React.FC<{
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}> = ({ children, className = '', onClick }) => {
  const interactive = !!onClick;
  return (
    <div
      onClick={onClick}
      className={`rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-[#1c2432] p-4 sm:p-5 ${
        interactive ? 'cursor-pointer hover:border-primary-300 dark:hover:border-primary-500/40 transition-colors' : ''
      } ${className}`}
    >
      {children}
    </div>
  );
};

const Eyebrow: React.FC<{ children: React.ReactNode; tone?: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' }> = ({
  children,
  tone = 'default',
}) => {
  const toneClass =
    tone === 'primary'
      ? 'text-primary-600 dark:text-primary-400'
      : tone === 'success'
      ? 'text-success-600 dark:text-success-400'
      : tone === 'warning'
      ? 'text-warning-600 dark:text-warning-500'
      : tone === 'error'
      ? 'text-error-600 dark:text-error-400'
      : tone === 'info'
      ? 'text-info-600 dark:text-info-400'
      : 'text-gray-500 dark:text-gray-500';
  return (
    <span className={`text-[10px] font-bold uppercase tracking-[0.18em] ${toneClass}`}>
      {children}
    </span>
  );
};

const CardHeader: React.FC<{
  eyebrow: string;
  eyebrowTone?: 'primary' | 'success' | 'warning' | 'error' | 'info';
  icon?: React.ComponentType<{ className?: string }>;
  right?: React.ReactNode;
}> = ({ eyebrow, eyebrowTone = 'primary', icon: Icon, right }) => (
  <div className="flex items-center justify-between mb-3 sm:mb-4">
    <div className="flex items-center gap-2">
      {Icon && (
        <div className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 flex items-center justify-center">
          <Icon className="w-3.5 h-3.5 text-gray-600 dark:text-gray-400" />
        </div>
      )}
      <Eyebrow tone={eyebrowTone}>{eyebrow}</Eyebrow>
    </div>
    {right}
  </div>
);

const KpiCard: React.FC<{
  label: string;
  value: string | number;
  sub?: string;
  accent?: 'primary' | 'info' | 'warning' | 'error';
}> = ({ label, value, sub, accent = 'primary' }) => {
  const accentClass =
    accent === 'primary'
      ? 'border-b-primary-500 dark:border-b-primary-500'
      : accent === 'info'
      ? 'border-b-info-500 dark:border-b-info-500'
      : accent === 'warning'
      ? 'border-b-warning-500 dark:border-b-warning-500'
      : 'border-b-error-500 dark:border-b-error-500';
  const valueColor =
    accent === 'warning'
      ? 'text-warning-600 dark:text-warning-400'
      : accent === 'error'
      ? 'text-error-600 dark:text-error-400'
      : accent === 'info'
      ? 'text-info-600 dark:text-info-400'
      : 'text-gray-900 dark:text-white';

  return (
    <div
      className={`rounded-2xl border border-gray-200 dark:border-slate-700 border-b-[3px] ${accentClass} bg-white dark:bg-[#1c2432] px-4 py-3`}
    >
      <Eyebrow>{label}</Eyebrow>
      <p className={`text-xl sm:text-2xl font-extrabold tabular-nums leading-tight mt-1 ${valueColor}`}>
        {value}
      </p>
      {sub && (
        <p className="text-[10px] text-gray-500 dark:text-gray-500 mt-0.5">{sub}</p>
      )}
    </div>
  );
};

const ComplianceRow: React.FC<{ label: string; value: string; ok: boolean }> = ({
  label,
  value,
  ok,
}) => (
  <div className="flex items-center gap-2">
    <span
      className={`w-1.5 h-1.5 rounded-full ${ok ? 'bg-primary-500' : 'bg-warning-500'}`}
    />
    <span className="text-gray-500 dark:text-gray-400">{label}</span>
    <span
      className={`ml-auto font-bold tabular-nums ${
        ok ? 'text-primary-600 dark:text-primary-400' : 'text-warning-600 dark:text-warning-400'
      }`}
    >
      {value}
    </span>
  </div>
);

const DateTile: React.FC<{ date: string }> = ({ date }) => {
  const d = new Date(date);
  const weekday = d.toLocaleDateString('de-DE', { weekday: 'short' }).toUpperCase();
  const month = d.toLocaleDateString('de-DE', { month: 'short' }).toUpperCase();
  return (
    <div className="flex-shrink-0 w-20 h-20 rounded-2xl bg-gray-50 dark:bg-slate-950/60 border border-gray-200 dark:border-slate-700 flex flex-col items-center justify-center">
      <div className="text-[10px] text-gray-500 dark:text-gray-500 uppercase tracking-wider">
        {weekday}
      </div>
      <div className="text-3xl font-extrabold text-gray-900 dark:text-white leading-none">
        {d.getDate().toString().padStart(2, '0')}
      </div>
      <div className="text-[10px] text-gray-500 dark:text-gray-500 uppercase tracking-wider">
        {month}
      </div>
    </div>
  );
};

export default Dashboard;
